import React, { useState, useEffect, useCallback } from 'react'

import { Search, Edit, Trash2, MapPin, Plus, Sparkles } from 'lucide-react'
import { useSelector } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'

import campaignsModalImg from '@/assets/images/modals/modal-campaings.png'
import { Campaign } from '@/core/api/entities/campaign/campaign'
import { PERMISSIONS } from '@/core/api/services/permissionService'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { usePaginateCampaigns } from '@/core/hooks/useCampaign'
import { usePermission } from '@/core/hooks/usePermission'
import { useDebounce } from '@/core/hooks/utils/useDebounce'
import { useTranslation } from '@/core/i18n/useTranslation'
import { RootState } from '@/core/store'
import { Button } from '@/shared/components/atoms/Button'
import { Input } from '@/shared/components/atoms/Input'
import { Separator } from '@/shared/components/atoms/Separator'
import { Skeleton } from '@/shared/components/atoms/Skeleton'
import { AccountFilterDropdown } from '@/shared/components/molecules/AccountFilterDropdown/AccountFilterDropdown'
import { CampaignCard } from '@/shared/components/molecules/CampaignCard/CampaignCard'
import { Card } from '@/shared/components/molecules/Card/Card'
import { PaginationControls } from '@/shared/components/molecules/PaginationControls/PaginationControls'
import SuccessModal from '@/shared/components/molecules/SuccessModal/SuccessModal'
import AuthenticatedLayout from '@/shared/components/organisms/Layout/AuthenticatedLayout/AuthenticatedLayout'
import { useWelcomeModal } from '@/shared/hooks/useWelcomeModal'

import { AssignCampaignModal } from './components/AssignCampaignModal'
import { RemoveCampaignModal } from './components/RemoveCampaignModal'

const DEFAULT_PAGE = 1
const DEFAULT_PAGE_SIZE = 25

const CampaignListPage: React.FC = () => {
	const { t } = useTranslation('campaigns')
	const { t: tCommon } = useTranslation('common')
	const navigate = useNavigate()
	const [searchParams, setSearchParams] = useSearchParams()
	const { hasAnyPermission, features } = usePermission()
	const canCreateCampaignBase = hasAnyPermission([
		PERMISSIONS.CREATE_ANNOUNCEMENTS,
		PERMISSIONS.MANAGE_CHILDREN_ANNOUNCEMENTS,
		PERMISSIONS.MANAGE_OWN_LEVEL_ANNOUNCEMENTS,
	])
	const canCreateCampaign = canCreateCampaignBase && features.campaign.create
	const canCreateAiCampaign =
		canCreateCampaignBase && features.campaign.createAi
	const canEditCampaign =
		hasAnyPermission([
			PERMISSIONS.MANAGE_CHILDREN_ANNOUNCEMENTS,
			PERMISSIONS.MANAGE_OWN_LEVEL_ANNOUNCEMENTS,
			PERMISSIONS.UPDATE_ANNOUNCEMENTS,
		]) && features.campaign.edit
	const canDeleteCampaign =
		hasAnyPermission([
			PERMISSIONS.MANAGE_CHILDREN_ANNOUNCEMENTS,
			PERMISSIONS.MANAGE_OWN_LEVEL_ANNOUNCEMENTS,
			PERMISSIONS.DESTROY_ANNOUNCEMENTS,
		]) && features.campaign.delete

	const fetchAllDataEnabled = useSelector(
		(state: RootState) => state.fetchAllData.enabled
	)
	const [accountFilter, setAccountFilter] = useState<number | null>(null)

	useEffect(() => {
		if (!fetchAllDataEnabled) {
			setAccountFilter(null)
		}
	}, [fetchAllDataEnabled])

	// Welcome modal with localStorage persistence and URL parameter support
	const {
		showModal: showWelcomeModal,
		handleCloseModal: handleCloseWelcomeModal,
	} = useWelcomeModal({
		modalKey: 'campaigns',
	})

	// Read pagination state from URL, with defaults
	const urlPage = parseInt(searchParams.get('page') || '', 10)
	const urlPerPage = parseInt(searchParams.get('per_page') || '', 10)
	const urlQuery = searchParams.get('q') || ''

	const currentPage = !isNaN(urlPage) && urlPage > 0 ? urlPage : DEFAULT_PAGE
	const pageSize =
		!isNaN(urlPerPage) && urlPerPage > 0 ? urlPerPage : DEFAULT_PAGE_SIZE

	const [searchQuery, setSearchQuery] = useState(urlQuery)
	const debouncedSearchQuery = useDebounce(searchQuery, 500)

	// Update URL when pagination/search changes
	const updateSearchParams = useCallback(
		(updates: { page?: number; per_page?: number; q?: string }) => {
			const newParams = new URLSearchParams(searchParams)

			if (updates.page !== undefined) {
				if (updates.page === DEFAULT_PAGE) {
					newParams.delete('page')
				} else {
					newParams.set('page', String(updates.page))
				}
			}

			if (updates.per_page !== undefined) {
				if (updates.per_page === DEFAULT_PAGE_SIZE) {
					newParams.delete('per_page')
				} else {
					newParams.set('per_page', String(updates.per_page))
				}
			}

			if (updates.q !== undefined) {
				if (updates.q === '') {
					newParams.delete('q')
				} else {
					newParams.set('q', updates.q)
				}
			}

			// Use replace to avoid polluting browser history with every pagination change
			setSearchParams(newParams, { replace: true })
		},
		[searchParams, setSearchParams]
	)

	// Sync search query to URL when debounced value changes
	useEffect(() => {
		// Only update if the debounced query differs from what's in the URL
		if (debouncedSearchQuery !== urlQuery) {
			updateSearchParams({ q: debouncedSearchQuery, page: DEFAULT_PAGE })
		}
	}, [debouncedSearchQuery, urlQuery, updateSearchParams])

	// Reset to page 1 when account filter changes (prevents empty page when new account has fewer pages)
	useEffect(() => {
		updateSearchParams({ page: DEFAULT_PAGE })
	}, [accountFilter])

	const onPageChange = useCallback(
		(newPage: number) => {
			updateSearchParams({ page: newPage })
		},
		[updateSearchParams]
	)

	const onPageSizeChange = useCallback(
		(newSize: number) => {
			// When page size changes, calculate which page should show the first item
			// of the current view to maintain continuity
			const currentFirstItemIndex = (currentPage - 1) * pageSize
			const newPage = Math.floor(currentFirstItemIndex / newSize) + 1
			updateSearchParams({ page: newPage, per_page: newSize })
		},
		[currentPage, pageSize, updateSearchParams]
	)

	const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false)
	const [selectedCampaignForRemove, setSelectedCampaignForRemove] = useState<{
		id: number
		name: string
	} | null>(null)
	const [isAssignModalOpen, setIsAssignModalOpen] = useState(false)
	const [selectedCampaignForAssign, setSelectedCampaignForAssign] =
		useState<Campaign | null>(null)

	const { data: campaignsResponse, isLoading } = usePaginateCampaigns({
		config: useAccountConfig({
			params: {
				page: currentPage,
				per_page: pageSize,
				query: debouncedSearchQuery,
				include: 'groups,adverts,account',
				...(accountFilter !== null && { account_id: accountFilter }),
			},
		}),
	})

	const campaigns = campaignsResponse?.data || []
	const paginationMeta = campaignsResponse?.meta?.pagination

	const handleCampaignClick = (campaign: Campaign) => {
		navigate(`/campaigns/${campaign.id}`)
	}

	const handleEdit = (campaign: Campaign) => {
		navigate(`/campaigns/${campaign.id}/edit`)
	}

	const handleManageAssignment = (campaign: Campaign) => {
		setSelectedCampaignForAssign(campaign)
		setIsAssignModalOpen(true)
	}

	const handleRemove = (campaign: Campaign) => {
		setSelectedCampaignForRemove({
			id: campaign.id,
			name: campaign.name,
		})
		setIsRemoveModalOpen(true)
	}

	const handleCreateAICampaign = () => {
		navigate('/campaigns/create-ai')
	}

	const handleCreateCampaign = () => {
		navigate('/campaigns/create')
	}

	return (
		<AuthenticatedLayout
			requireAuth={true}
			topAppBarLeftContent={
				<h1 className="md:hidden text-2xl font-semibold tracking-tight">
					{t('list.title')}
				</h1>
			}
		>
			<div className="w-full md:px-3 2xl:pl-6 2xl:pr-6 pb-1 md:py-3 2xl:py-6">
				<div className="max-w-[1400px] mx-auto w-full">
					<div className="md:border md:rounded-xl overflow-hidden">
						<div className="hidden md:flex bg-card lg:py-6 py-3 lg:px-8 px-4 items-center justify-between">
							<div className="space-y-0">
								<h2 className="flex items-center text-2xl font-normal tracking-tight md:text-3xl">
									{t('list.title')}
								</h2>
							</div>
						</div>

						<Separator className="hidden md:block" />

						<div className="px-4 py-4 lg:px-8 lg:py-6 md:bg-card space-y-6">
							<div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8 lg:h-8">
								<div className="flex items-center gap-3 flex-1 md:max-w-lg h-full">
									<div className="relative flex-1 h-full">
										<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
										<Input
											type="search"
											placeholder={t('list.searchPlaceholder')}
											value={searchQuery}
											onChange={(e) => setSearchQuery(e.target.value)}
											className="pl-9"
										/>
									</div>
									{fetchAllDataEnabled && (
										<AccountFilterDropdown
											value={accountFilter}
											onChange={setAccountFilter}
										/>
									)}
								</div>
								<div className="hidden sm:flex items-center gap-2">
									{canCreateAiCampaign && (
										<Button
											variant="outline"
											size="action"
											className="rounded-full"
											onClick={handleCreateAICampaign}
										>
											<Sparkles className="mr-1.5 !h-4 !w-4" />
											{t('list.createAiAd')}
										</Button>
									)}
									{canCreateCampaign && (
										<Button
											variant="action"
											size="action"
											onClick={handleCreateCampaign}
										>
											<Plus className="mr-1.5 !h-4 !w-4" />
											{t('list.createAdCampaign')}
										</Button>
									)}
								</div>
								{canCreateAiCampaign && (
									<div className="sm:hidden">
										<Button
											variant="outline"
											size="action"
											className="rounded-full w-full"
											onClick={handleCreateAICampaign}
										>
											<Sparkles className="mr-1.5 !h-4 !w-4" />
											{t('list.createAiAd')}
										</Button>
									</div>
								)}
							</div>

							{isLoading && (
								<div className="space-y-4">
									{[...Array(5)].map((_, i) => (
										<Card key={i} className="overflow-hidden bg-card">
											<div className="px-6 py-4">
												<div className="flex items-start justify-between">
													<div className="flex-1 flex-col lg:flex-row justify-between min-w-0">
														<div>
															<div className="flex items-center gap-3 mb-2">
																<Skeleton className="h-5 w-20" />
															</div>
															<Skeleton className="h-3 w-24 lg:w-32 mb-1" />
															<Skeleton className="h-6 w-48 lg:h-7 lg:w-64 mb-4" />

															<div className="flex flex-col items-end lg:gap-8">
																{/* Created time - hidden on mobile, matches actual card */}
																<Skeleton className="hidden lg:block h-4 w-36 mr-4" />
																<div className="flex items-end gap-4 lg:gap-10 text-sm">
																	<div>
																		<Skeleton className="h-3 lg:h-4 w-16 lg:w-20 mb-1" />
																		<Skeleton className="h-4 lg:h-5 w-6 lg:w-8" />
																	</div>
																	<div>
																		<Skeleton className="h-3 lg:h-4 w-16 lg:w-20 mb-1" />
																		<Skeleton className="h-4 lg:h-5 w-24 lg:w-32" />
																	</div>
																	<div>
																		<Skeleton className="h-3 lg:h-4 w-16 lg:w-20 mb-1" />
																		<Skeleton className="h-4 lg:h-5 w-24 lg:w-32" />
																	</div>
																</div>
															</div>
														</div>
													</div>
													<Skeleton className="h-8 w-8 rounded-full" />
												</div>
											</div>
										</Card>
									))}
								</div>
							)}

							{!isLoading &&
								campaigns.length === 0 &&
								debouncedSearchQuery.trim().length === 0 && (
									<div className="flex flex-col items-center justify-center px-6 bg-card-dark rounded-2xl min-h-[calc(100vh-280px)]">
										<div className="text-center space-y-4 max-w-md">
											<div className="mx-auto flex h-20 w-20 items-center justify-center rounded-2xl bg-card/60 border border-border/60">
												<Sparkles className="h-7 w-7 text-muted-foreground" />
											</div>
											<h3 className="text-2xl font-semibold">
												{t('list.empty.title')}
											</h3>
											<p className="text-sm text-muted-foreground">
												{t('list.empty.description')}
											</p>
											{canCreateCampaign && (
												<Button
													variant="default"
													className="rounded-full"
													onClick={handleCreateCampaign}
												>
													<Plus className="mr-2 h-4 w-4" />
													{t('list.empty.action')}
												</Button>
											)}
										</div>
									</div>
								)}

							{!isLoading && campaigns.length > 0 && (
								<div className="space-y-4">
									{campaigns.map((campaign) => (
										<CampaignCard
											key={campaign.id}
											campaign={campaign}
											onClick={() => handleCampaignClick(campaign)}
											menuItems={[
												...(canEditCampaign
													? [
															{
																label: t('list.actions.edit'),
																icon: Edit,
																onClick: () => handleEdit(campaign),
															},
														]
													: []),
												...(canEditCampaign
													? [
															{
																label: t('list.actions.manageAssignment'),
																icon: MapPin,
																onClick: () => handleManageAssignment(campaign),
															},
														]
													: []),
												...(canDeleteCampaign
													? [
															{
																label: t('list.actions.delete'),
																icon: Trash2,
																onClick: () => handleRemove(campaign),
																variant: 'destructive' as const,
															},
														]
													: []),
											]}
										/>
									))}
									<PaginationControls
										currentPage={currentPage}
										totalPages={paginationMeta?.total_pages ?? 1}
										onPageChange={onPageChange}
										pageSize={pageSize}
										onPageSizeChange={onPageSizeChange}
										className="pt-6"
									/>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
			{canCreateCampaign && (
				<Button
					variant="action"
					size="action"
					onClick={handleCreateCampaign}
					className="md:hidden fixed bottom-[110px] right-4 z-40 flex h-14 w-14 items-center justify-center rounded-full shadow-lg transition hover:opacity-90"
				>
					<Plus className="!h-6 !w-6 !m-0" />
				</Button>
			)}

			<RemoveCampaignModal
				open={isRemoveModalOpen}
				onOpenChange={setIsRemoveModalOpen}
				campaignId={selectedCampaignForRemove?.id}
				campaignName={selectedCampaignForRemove?.name}
			/>
			<AssignCampaignModal
				open={isAssignModalOpen}
				onOpenChange={(open) => {
					if (!open) {
						setSelectedCampaignForAssign(null)
					}
					setIsAssignModalOpen(open)
				}}
				campaignId={selectedCampaignForAssign?.id}
				campaignName={selectedCampaignForAssign?.name}
				initialGroupIds={
					selectedCampaignForAssign?.groups?.map((group) => group.id) ?? []
				}
			/>

			<SuccessModal
				visible={showWelcomeModal}
				image={campaignsModalImg}
				imageAlt={tCommon('welcomeModals.campaigns.title')}
				title={tCommon('welcomeModals.campaigns.title')}
				description={tCommon('welcomeModals.campaigns.description')}
				primaryAction={{
					label: tCommon('welcomeModals.campaigns.continue'),
					onClick: handleCloseWelcomeModal,
				}}
				onClose={handleCloseWelcomeModal}
			/>
		</AuthenticatedLayout>
	)
}

export default CampaignListPage
